/**
 * Game Sound Effects using Web Audio API
 * Generates fun, retro-style sounds for gameplay
 */

class GameSounds {
  constructor() {
    this.audioContext = null;
    this.enabled = true;
    this.volume = 0.5;
  }

  init() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    }
    return this.audioContext;
  }

  setEnabled(enabled) {
    this.enabled = enabled;
  }

  setVolume(vol) {
    this.volume = Math.max(0, Math.min(1, vol));
  }

  // Play a tone with given frequency, duration, and type
  playTone(frequency, duration, type = 'sine', gainValue = null) {
    if (!this.enabled) return;
    
    try {
      const ctx = this.init();
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      oscillator.type = type;
      oscillator.frequency.setValueAtTime(frequency, ctx.currentTime);
      
      const vol = gainValue !== null ? gainValue : this.volume;
      gainNode.gain.setValueAtTime(vol, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration);
      
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + duration);
    } catch (e) {
      console.warn('Sound playback failed:', e);
    }
  }

  // Correct answer - happy ascending notes
  playCorrect() {
    if (!this.enabled) return;
    
    const ctx = this.init();
    const now = ctx.currentTime;
    
    // Play a cheerful ascending arpeggio
    [523, 659, 784, 1047].forEach((freq, i) => {
      setTimeout(() => {
        this.playTone(freq, 0.15, 'sine', this.volume * 0.4);
      }, i * 80);
    });
  }

  // Wrong answer - descending buzz
  playWrong() {
    if (!this.enabled) return;
    
    this.playTone(200, 0.3, 'sawtooth', this.volume * 0.3);
    setTimeout(() => {
      this.playTone(150, 0.3, 'sawtooth', this.volume * 0.25);
    }, 100);
  }

  // Streak bonus - exciting cascade
  playStreak() {
    if (!this.enabled) return;
    
    // Fast ascending sparkle
    [440, 554, 659, 880, 1109, 1319].forEach((freq, i) => {
      setTimeout(() => {
        this.playTone(freq, 0.1, 'sine', this.volume * 0.35);
      }, i * 50);
    });
  }

  // Timer tick (last 10 seconds)
  playTick() {
    if (!this.enabled) return;
    this.playTone(800, 0.05, 'square', this.volume * 0.2);
  }

  // Timer warning (last 5 seconds)
  playWarning() {
    if (!this.enabled) return;
    this.playTone(600, 0.1, 'square', this.volume * 0.3);
    setTimeout(() => {
      this.playTone(600, 0.1, 'square', this.volume * 0.3);
    }, 150);
  }

  // Game start fanfare
  playGameStart() {
    if (!this.enabled) return;
    
    const notes = [262, 330, 392, 523];
    notes.forEach((freq, i) => {
      setTimeout(() => {
        this.playTone(freq, 0.2, 'sine', this.volume * 0.4);
      }, i * 150);
    });
  }

  // Question appear whoosh
  playNewQuestion() {
    if (!this.enabled) return;
    
    const ctx = this.init();
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(200, ctx.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(800, ctx.currentTime + 0.15);
    
    gainNode.gain.setValueAtTime(this.volume * 0.3, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
    
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.2);
  }

  // Button click
  playClick() {
    if (!this.enabled) return;
    this.playTone(600, 0.05, 'sine', this.volume * 0.2);
  }

  // Badge earned celebration
  playBadgeEarned() {
    if (!this.enabled) return;
    
    // Triumphant fanfare
    const melody = [523, 659, 784, 1047, 784, 1047];
    melody.forEach((freq, i) => {
      setTimeout(() => {
        this.playTone(freq, 0.15, 'sine', this.volume * 0.4);
      }, i * 100);
    });
  }

  // Game complete celebration
  playGameComplete() {
    if (!this.enabled) return;
    
    // Victory fanfare
    const melody = [523, 523, 523, 523, 415, 466, 523, 466, 523];
    const durations = [0.15, 0.15, 0.15, 0.3, 0.15, 0.15, 0.15, 0.15, 0.4];
    let time = 0;
    
    melody.forEach((freq, i) => {
      setTimeout(() => {
        this.playTone(freq, durations[i], 'sine', this.volume * 0.4);
      }, time);
      time += durations[i] * 1000 * 0.7;
    });
  }

  // Points pop
  playPoints() {
    if (!this.enabled) return;
    this.playTone(880, 0.08, 'sine', this.volume * 0.25);
    setTimeout(() => {
      this.playTone(1100, 0.08, 'sine', this.volume * 0.25);
    }, 60);
  }

  // Time's up
  playTimeUp() {
    if (!this.enabled) return;
    this.playTone(300, 0.4, 'sawtooth', this.volume * 0.3);
  }
}

// Singleton instance
const gameSounds = new GameSounds();

export default gameSounds;
